import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Flex,
  VStack,
  HStack,
  Badge,
  Textarea,
  Input,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  Icon,
  Avatar,
  Card,
  CardBody,
  Tooltip,
  IconButton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  MdEmail,
  MdSend,
  MdCheckCircle,
  MdAccessTime,
  MdContentCopy,
  MdReply,
  MdSubject,
} from 'react-icons/md';
import { useReplyFeedback } from 'queries/feedback.query';

export default function ViewFeedbackModal({ isOpen, onClose, feedback, onReplySuccess }) {
  const toast = useToast();
  const { mutate: replyMutate, isPending } = useReplyFeedback();

  // Color mode hooks MUST be declared at the top level
  const cardBg = useColorModeValue('gray.50', 'navy.900');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textSubtle = useColorModeValue('gray.500', 'gray.400');
  const replyBoxBg = useColorModeValue('green.50', 'rgba(72, 187, 120, 0.1)');
  const replyBorderColor = useColorModeValue('green.200', 'green.600');
  const messageBoxBg = useColorModeValue('white', 'navy.800');
  const replyFormBg = useColorModeValue('brand.50', 'whiteAlpha.50');
  const inputBg = useColorModeValue('white', 'navy.900');

  const [replyMessage, setReplyMessage] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);

  useEffect(() => {
    if (feedback) {
      const defaultSubj = feedback.subject
        ? `Re: ${feedback.subject}`
        : `Response to your inquiry - ${feedback.site_name || 'Bamyan Entertainment'}`;
      setReplySubject(feedback.reply_subject || defaultSubj);
      setReplyMessage('');
      setShowReplyForm(!feedback.reply_message);
    }
  }, [feedback]);

  if (!feedback) return null;

  const isReplied = Boolean(feedback.reply_message || feedback.status === 'replied');

  const handleCopyEmail = () => {
    if (feedback.email) {
      navigator.clipboard.writeText(feedback.email);
      toast({
        title: 'Email Copied',
        description: `${feedback.email} copied to clipboard.`,
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleQuickTemplate = (templateType) => {
    const siteName = feedback.site_name || 'Bamyan Entertainment';
    const customerName = feedback.name || 'valued customer';

    if (templateType === 'thanks') {
      setReplyMessage(
        `Dear ${customerName},\n\nThank you for reaching out to ${siteName}. We greatly appreciate your feedback and comments. Our team has reviewed your message and we are continuously working to improve our events and services.\n\nBest regards,\nThe ${siteName} Team`
      );
    } else if (templateType === 'resolved') {
      setReplyMessage(
        `Dear ${customerName},\n\nThank you for your inquiry. Regarding the matter you contacted us about, we would like to inform you that it has been reviewed and resolved by our support team.\n\nIf you have any further questions or need additional assistance, please don't hesitate to reply directly to this email.\n\nBest regards,\nThe ${siteName} Team`
      );
    } else if (templateType === 'more_info') {
      setReplyMessage(
        `Dear ${customerName},\n\nThank you for contacting ${siteName}. To assist you further with your request, could you please provide us with a few more details (e.g. order number, ticket code, or specific event name)?\n\nLooking forward to hearing from you.\n\nBest regards,\nThe ${siteName} Support Team`
      );
    }
  };

  const handleSendReply = (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      toast({
        title: 'Message Required',
        description: 'Please type a reply message before sending.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    replyMutate(
      {
        id: feedback.id,
        data: {
          reply_message: replyMessage.trim(),
          reply_subject: replySubject.trim(),
        },
      },
      {
        onSuccess: () => {
          toast({
            title: 'Reply Sent Successfully!',
            description: `Official email response sent to ${feedback.email} from ${feedback.site_email || 'site email'}.`,
            status: 'success',
            duration: 4000,
            isClosable: true,
            position: 'top',
          });
          setReplyMessage('');
          if (onReplySuccess) onReplySuccess();
          onClose();
        },
        onError: (err) => {
          toast({
            title: 'Failed to Send Reply',
            description: err?.response?.data?.message || 'Error sending reply email. Check server mail configuration.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          });
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" overflow="hidden">
        {/* Modal Header */}
        <ModalHeader
          bg={cardBg}
          borderBottomWidth="1px"
          borderColor={borderColor}
          py={4}
        >
          <Flex justify="space-between" align="center" pr={6}>
            <HStack spacing={3}>
              <Icon as={MdEmail} w={6} h={6} color="brand.500" />
              <Box>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  Feedback Details
                </Text>
                <Text fontSize="xs" color={textSubtle}>
                  Message ID: #{feedback.id} &bull; Received on {feedback.created_at ? new Date(feedback.created_at).toLocaleString() : 'N/A'}
                </Text>
              </Box>
            </HStack>
            <Badge
              colorScheme={isReplied ? 'green' : 'orange'}
              fontSize="xs"
              px={3}
              py={1}
              borderRadius="full"
              display="flex"
              alignItems="center"
              gap={1}
            >
              {isReplied ? (
                <>
                  <Icon as={MdCheckCircle} /> Replied
                </>
              ) : (
                <>
                  <Icon as={MdAccessTime} /> Pending Response
                </>
              )}
            </Badge>
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />

        <ModalBody py={5} px={6}>
          <VStack spacing={5} align="stretch">
            {/* Sender Profile Information */}
            <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" shadow="none">
              <CardBody p={4}>
                <Flex justify="space-between" align={{ base: 'start', sm: 'center' }} direction={{ base: 'column', sm: 'row' }} gap={3}>
                  <HStack spacing={3}>
                    <Avatar
                      name={feedback.name}
                      bg="brand.500"
                      color="white"
                      size="md"
                    />
                    <Box>
                      <Text fontWeight="bold" fontSize="md" color={textColor}>
                        {feedback.name || 'Anonymous Sender'}
                      </Text>
                      <HStack spacing={2} mt={0.5}>
                        <Text fontSize="sm" color="blue.500" as="a" href={`mailto:${feedback.email}`}>
                          {feedback.email}
                        </Text>
                        <Tooltip label="Copy email">
                          <IconButton
                            icon={<MdContentCopy />}
                            size="xs"
                            variant="ghost"
                            aria-label="Copy email address"
                            onClick={handleCopyEmail}
                          />
                        </Tooltip>
                      </HStack>
                    </Box>
                  </HStack>

                  <HStack spacing={2} fontSize="xs" color={textSubtle}>
                    <Icon as={MdAccessTime} />
                    <Text>
                      {feedback.created_at ? new Date(feedback.created_at).toLocaleDateString(undefined, { dateStyle: 'long' }) : 'N/A'}
                    </Text>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>

            {/* Subject & Original Message Content */}
            <Box>
              <HStack spacing={2} mb={2}>
                <Icon as={MdSubject} color="brand.500" />
                <Text fontWeight="bold" fontSize="sm" color={textSubtle} textTransform="uppercase" letterSpacing="wider">
                  Subject:
                </Text>
                <Text fontWeight="bold" fontSize="md" color={textColor}>
                  {feedback.subject || '(No Subject Provided)'}
                </Text>
              </HStack>

              <Box
                p={4}
                bg={messageBoxBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                minH="100px"
              >
                <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap" lineHeight="1.8">
                  {feedback.message}
                </Text>
              </Box>
            </Box>

            {/* Previous Reply History (if already replied) */}
            {feedback.reply_message && (
              <Box
                p={4}
                bg={replyBoxBg}
                borderWidth="1px"
                borderColor={replyBorderColor}
                borderRadius="lg"
              >
                <Flex justify="space-between" align="center" mb={2}>
                  <HStack spacing={2}>
                    <Icon as={MdCheckCircle} color="green.500" />
                    <Text fontWeight="bold" fontSize="sm" color="green.700" _dark={{ color: 'green.300' }}>
                      Previous Response Sent via Site Email
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color={textSubtle}>
                    Replied: {feedback.replied_at ? new Date(feedback.replied_at).toLocaleString() : 'Recently'}
                  </Text>
                </Flex>
                {feedback.reply_subject && (
                  <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={1}>
                    Subject: {feedback.reply_subject}
                  </Text>
                )}
                <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap" lineHeight="1.7" pl={2}>
                  {feedback.reply_message}
                </Text>

                {!showReplyForm && (
                  <Button
                    mt={3}
                    size="xs"
                    leftIcon={<MdReply />}
                    colorScheme="brand"
                    variant="outline"
                    onClick={() => setShowReplyForm(true)}
                  >
                    Send Another Response
                  </Button>
                )}
              </Box>
            )}

            {/* Email Reply Form */}
            {(!isReplied || showReplyForm) && (
              <Box
                as="form"
                onSubmit={handleSendReply}
                p={4}
                borderRadius="lg"
                borderWidth="1px"
                borderColor="brand.300"
                bg={replyFormBg}
              >
                <Flex justify="space-between" align="center" mb={3}>
                  <HStack spacing={2}>
                    <Icon as={MdReply} color="brand.500" />
                    <Text fontWeight="bold" fontSize="md" color={textColor}>
                      Reply to Customer via Site Email
                    </Text>
                  </HStack>
                  <Badge colorScheme="blue" fontSize="10px" px={2} py={0.5} borderRadius="md">
                    From: {feedback.site_email || 'info@bamyan-entertainment.com'}
                  </Badge>
                </Flex>

                <VStack spacing={3} align="stretch">
                  <FormControl isRequired>
                    <FormLabel fontSize="xs" fontWeight="bold" mb={1}>
                      Email Subject
                    </FormLabel>
                    <Input
                      size="sm"
                      bg={inputBg}
                      value={replySubject}
                      onChange={(e) => setReplySubject(e.target.value)}
                      placeholder="Email subject..."
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <Flex justify="space-between" align="center" mb={1}>
                      <FormLabel fontSize="xs" fontWeight="bold" mb={0}>
                        Response Message
                      </FormLabel>
                      <Text fontSize="11px" color={textSubtle}>
                        Will be delivered directly to <strong>{feedback.email}</strong>
                      </Text>
                    </Flex>
                    <Textarea
                      size="sm"
                      rows={5}
                      bg={inputBg}
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Write your response to the customer here. It will be sent from the official site email address..."
                    />
                  </FormControl>

                  {/* Quick Reply Templates */}
                  <Box>
                    <Text fontSize="xs" color={textSubtle} mb={1.5}>
                      Quick Response Templates:
                    </Text>
                    <Wrap spacing={2}>
                      <WrapItem>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="gray"
                          onClick={() => handleQuickTemplate('thanks')}
                        >
                          🙏 Thank You & Greeting
                        </Button>
                      </WrapItem>
                      <WrapItem>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="gray"
                          onClick={() => handleQuickTemplate('resolved')}
                        >
                          ✅ Inquiry Resolved
                        </Button>
                      </WrapItem>
                      <WrapItem>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="gray"
                          onClick={() => handleQuickTemplate('more_info')}
                        >
                          ℹ️ Request More Details
                        </Button>
                      </WrapItem>
                    </Wrap>
                  </Box>

                  <Flex justify="flex-end" pt={2} gap={2}>
                    {showReplyForm && isReplied && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowReplyForm(false)}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      size="sm"
                      colorScheme="brand"
                      type="submit"
                      leftIcon={<MdSend />}
                      isLoading={isPending}
                      loadingText="Sending Email..."
                    >
                      Send Reply via Site Email
                    </Button>
                  </Flex>
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter bg={cardBg} borderTopWidth="1px" borderColor={borderColor} py={3}>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
